package fyi.moko.hotpl.domain.model

/**
 * What a user-added photo is a picture of, and therefore where it belongs.
 *
 * ── 이 분류가 실제로 결정하는 것 ─────────────────────────────
 *  · 사진이 **가게에 붙는지 방문에 붙는지**([group]). 간판·내부·메뉴판은 시간이 지나도 그
 *    가게의 모습이라 가게 갤러리에 남고, 음식과 사람은 그날 방문에 묶인다.
 *  · 카테고리 자체는 사용자에게 거의 보이지 않는다. 화면이 말하는 건 "가게 카드에 보입니다"
 *    또는 "이 방문 기록에만 보입니다"이지 `외관`이 아니다.
 *
 * 실측(사진 33장 × 실행 4회, VersionSandbox `docs/photo_classify_probe.md`): 카테고리를
 * 정확히 맞힌 건 32/33이지만 **[group]을 맞힌 건 132건 중 131건**이다. 4회 내내 틀리던 사진
 * 한 장은 외관 → 내부 혼동이라 둘 다 [PhotoGroup.PLACE]로, 화면에서는 아무 일도 일으키지
 * 않는다. 그래서 분류 정확도를 더 끌어올리는 것보다 [group] 경계를 지키는 게 중요하다.
 */
enum class PhotoKind(val group: PhotoGroup) {
    /** 간판·건물 정면. 다시 찾아왔을 때 가게를 알아보게 해 준다. */
    STOREFRONT(PhotoGroup.PLACE),

    /** 가게 안 공간·분위기. */
    INTERIOR(PhotoGroup.PLACE),

    /** 메뉴판. 인쇄물이든 화면이든. */
    MENU(PhotoGroup.PLACE),

    /** 그날 먹은 것. 음료도 포함한다 — 나누면 "라떼 + 머핀" 같은 사진에서 경계 분쟁이 생긴다. */
    FOOD_DRINK(PhotoGroup.VISIT),

    /** 인물이 주인공인 사진. 대표 사진에서 제외하기 위해 존재하는 칸이기도 하다. */
    PEOPLE(PhotoGroup.VISIT),

    /** 위 어디에도 해당하지 않음. 사용자가 직접 정한다. */
    OTHER(PhotoGroup.UNDECIDED),
    ;

    companion object {
        /**
         * 대표 사진 우선순위. 앞에 있을수록 먼저 고른다.
         *
         * [PEOPLE]과 [OTHER]는 **목록에 없다 = 자동으로는 절대 대표가 되지 않는다.** 가게 카드가
         * 일행 단체 사진이 되면 안 되기 때문이다. 사용자가 직접 지정하는 건 별개로 허용한다.
         *
         * [STOREFRONT]가 [FOOD_DRINK]보다 앞인 이유: 음식 사진이 더 눈에 띄지만, 카드가 하는
         * 일은 목록에서 "다시 갈 그 가게"를 알아보게 하는 것이다. [MENU]는 문서라 맨 뒤다.
         */
        val COVER_PRIORITY: List<PhotoKind> = listOf(STOREFRONT, FOOD_DRINK, INTERIOR, MENU)

        /** [COVER_PRIORITY] 순서로 고른 대표 사진 후보, 또는 쓸 만한 게 없으면 null. */
        fun <T> pickCover(items: List<T>, kindOf: (T) -> PhotoKind?): T? {
            for (kind in COVER_PRIORITY) {
                val hit = items.firstOrNull { kindOf(it) == kind }
                if (hit != null) return hit
            }
            return null
        }
    }
}

/** 사진이 가게에 붙는지, 특정 방문에 붙는지. 화면이 실제로 갈라 보여주는 축이다. */
enum class PhotoGroup {
    /** 가게 갤러리. 대표 사진 후보이기도 하다. */
    PLACE,

    /** 그 방문 기록에만 보인다. */
    VISIT,

    /** 알아보지 못했다. 사용자가 정할 때까지 어느 쪽에도 넣지 않는다. */
    UNDECIDED,
}
